// Guía interactiva (tour de bienvenida con zonas resaltadas). Aquí vive el
// estado; el motor que pinta el resaltado y la tarjeta es
// components/guia/GuiaInteractiva.js, montado una vez en Layout.
// El estado (pasos serializables, índice, vista previa...) se guarda en
// sessionStorage['guia_estado'] para seguir tras navegar o recargar; al montar,
// si hay estado guardado, se reanuda. "Saltar la guía" lo deja en
// sessionStorage['guia_pospuesta'] (y 'tour_pospuesto') para retomarlo desde
// Mi Inicio en el paso en que se dejó, y lo guarda en el servidor
// (POST /portal/tour-posponer): sessionStorage se pierde al cerrar la app del
// móvil y, sin eso, Mi Inicio volvía a abrir la guía cada vez. Al terminar
// (fuera de la vista previa) se marca como vista en el servidor
// (POST /portal/tour-visto), que también borra lo pospuesto.
// Novedades (`novedad: true`, components/guia/NovedadesPendientes.js): mini
// guía de 1-2 tarjetas de una utilidad nueva. No toca nada del tour: al
// terminar no llama a tour-visto ni a lo pospuesto, "Cerrar" (posponerGuia)
// equivale a terminar, y no se guarda en sessionStorage (si se recarga, se
// vuelve a lanzar porque aún no está marcada como vista).
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { portalAPI } from '../api/api';
import { useAuth } from './AuthContext';

const CLAVE_ESTADO = 'guia_estado';
const CLAVE_POSPUESTA = 'guia_pospuesta';
export const CLAVE_TOUR_POSPUESTO = 'tour_pospuesto';
// Se lanza en window al terminar la guía (Mi Inicio recarga sus datos)
export const EVENTO_GUIA_TERMINADA = 'guia-terminada';

const leer = (clave) => {
  try {
    return window.sessionStorage.getItem(clave);
  } catch (e) {
    return null;
  }
};

const escribir = (clave, valor) => {
  try {
    if (valor === null || valor === undefined) window.sessionStorage.removeItem(clave);
    else window.sessionStorage.setItem(clave, valor);
  } catch (e) {
    // Sin sessionStorage (modo privado): la guía funciona igual, sin recordar el paso
  }
};

const leerJSON = (clave) => {
  try {
    const texto = leer(clave);
    return texto ? JSON.parse(texto) : null;
  } catch (e) {
    return null;
  }
};

const escribirJSON = (clave, valor) => {
  try {
    escribir(clave, valor ? JSON.stringify(valor) : null);
  } catch (e) {
    escribir(clave, null);
  }
};

const estadoValido = (e) => !!e && Array.isArray(e.pasos) && e.pasos.length > 0
  && Number.isInteger(e.indice) && e.indice >= 0 && e.indice < e.pasos.length;

const idUsuario = (user) => (user ? String(user.id || user.email || user.empleado_id || '') : '');

const GuiaContext = createContext(null);

// Fuera del provider (pruebas, rutas sueltas) no rompe: guía inactiva
const SIN_GUIA = {
  estado: null,
  guiaActiva: false,
  hayGuiaPospuesta: false,
  iniciarGuia: () => {},
  pararGuia: () => {},
  irAPaso: () => {},
  actualizarGuia: () => {},
  posponerGuia: () => {},
  retomarGuia: () => {},
  terminarGuia: async () => {},
};

export const useGuia = () => useContext(GuiaContext) || SIN_GUIA;

export const GuiaProvider = ({ children }) => {
  const { user } = useAuth();
  const usuario = idUsuario(user);
  const [estado, setEstado] = useState(() => {
    const e = leerJSON(CLAVE_ESTADO);
    if (!estadoValido(e) || e.novedad) return null;
    // Al recargar se vuelve a la pantalla del paso por el camino corto
    return { ...e, directa: true, rutaOk: false, perdidas: 0 };
  });
  const [hayGuiaPospuesta, setHayGuiaPospuesta] = useState(
    () => !!leerJSON(CLAVE_POSPUESTA) || leer(CLAVE_TOUR_POSPUESTO) === '1',
  );
  const estadoRef = useRef(estado);
  estadoRef.current = estado;
  const alTerminarRef = useRef(null);

  useEffect(() => {
    escribirJSON(CLAVE_ESTADO, estado && !estado.novedad ? estado : null);
  }, [estado]);

  // Sin sesión o con otra persona: fuera la guía guardada
  useEffect(() => {
    if (!user) {
      setEstado(null);
      escribirJSON(CLAVE_POSPUESTA, null);
      setHayGuiaPospuesta(false);
      return;
    }
    setEstado((e) => (e && e.usuario && e.usuario !== usuario ? null : e));
  }, [user, usuario]);

  const iniciarGuia = useCallback(({
    pasos, previsualizar = false, nombre = '', alTerminar = null, indice = 0, novedad = false,
  } = {}) => {
    if (!Array.isArray(pasos) || pasos.length === 0) return;
    alTerminarRef.current = typeof alTerminar === 'function' ? alTerminar : null;
    if (!previsualizar && !novedad) {
      escribirJSON(CLAVE_POSPUESTA, null);
      escribir(CLAVE_TOUR_POSPUESTO, null);
      setHayGuiaPospuesta(false);
    }
    setEstado({
      pasos,
      indice: Math.min(Math.max(0, Number(indice) || 0), pasos.length - 1),
      previsualizar: !!previsualizar,
      novedad: !!novedad,
      nombre: nombre || '',
      usuario,
      fallidos: [],
      sentido: 1,
      directa: true,
      rutaOk: false,
      perdidas: 0,
    });
  }, [usuario]);

  const pararGuia = useCallback(() => {
    alTerminarRef.current = null;
    setEstado(null);
  }, []);

  // `directa`: llegar a la pantalla del paso navegando sin pedir tocar el menú
  // (al empezar, al volver Atrás o al retomar)
  const irAPaso = useCallback((indice, { directa = false, sentido = 1 } = {}) => {
    setEstado((e) => {
      if (!e) return e;
      const i = Math.min(Math.max(0, indice), e.pasos.length - 1);
      return { ...e, indice: i, directa, sentido, rutaOk: false, perdidas: 0 };
    });
  }, []);

  const actualizarGuia = useCallback((cambios) => {
    setEstado((e) => (e ? { ...e, ...(typeof cambios === 'function' ? cambios(e) : cambios) } : e));
  }, []);

  // En una novedad no hay "para luego": cerrar es darla por vista
  const cerrarNovedad = useCallback(() => {
    const alTerminar = alTerminarRef.current;
    alTerminarRef.current = null;
    setEstado(null);
    if (alTerminar) {
      try { alTerminar(); } catch (err) { console.error('Error al cerrar la novedad:', err); }
    }
  }, []);

  const posponerGuia = useCallback(() => {
    const e = estadoRef.current;
    if (e && e.novedad) {
      cerrarNovedad();
      return;
    }
    if (e && !e.previsualizar) {
      escribirJSON(CLAVE_POSPUESTA, { ...e, directa: true, rutaOk: false, perdidas: 0 });
      escribir(CLAVE_TOUR_POSPUESTO, '1');
      setHayGuiaPospuesta(true);
      // Sin esperar: si falla, solo se pierde que no vuelva a abrirse sola en otra sesión
      portalAPI.tourPosponer().catch((err) => {
        console.warn('No se ha podido guardar en el servidor que la guía queda para luego.', err);
      });
    }
    alTerminarRef.current = null;
    setEstado(null);
  }, [cerrarNovedad]);

  const retomarGuia = useCallback((pasosPorDefecto) => {
    const guardada = leerJSON(CLAVE_POSPUESTA);
    escribirJSON(CLAVE_POSPUESTA, null);
    escribir(CLAVE_TOUR_POSPUESTO, null);
    setHayGuiaPospuesta(false);
    if (estadoValido(guardada) && (!guardada.usuario || guardada.usuario === usuario)) {
      setEstado({ ...guardada, usuario, directa: true, rutaOk: false, perdidas: 0, sentido: 1 });
      return;
    }
    if (Array.isArray(pasosPorDefecto) && pasosPorDefecto.length > 0) {
      setEstado({
        pasos: pasosPorDefecto,
        indice: 0,
        previsualizar: false,
        nombre: '',
        usuario,
        fallidos: [],
        sentido: 1,
        directa: true,
        rutaOk: false,
        perdidas: 0,
      });
    }
  }, [usuario]);

  // Lanza el error del servidor para que la tarjeta lo enseñe
  const terminarGuia = useCallback(async () => {
    const e = estadoRef.current;
    if (e && e.novedad) {
      cerrarNovedad();
      return;
    }
    if (e && !e.previsualizar) {
      await portalAPI.tourVisto();
      escribirJSON(CLAVE_POSPUESTA, null);
      escribir(CLAVE_TOUR_POSPUESTO, null);
      setHayGuiaPospuesta(false);
    }
    const alTerminar = alTerminarRef.current;
    alTerminarRef.current = null;
    setEstado(null);
    if (alTerminar) {
      try { alTerminar(); } catch (err) { console.error('Error al cerrar la guía:', err); }
    }
    if (e && !e.previsualizar) {
      try { window.dispatchEvent(new CustomEvent(EVENTO_GUIA_TERMINADA)); } catch (err) { /* navegador antiguo */ }
    }
  }, [cerrarNovedad]);

  const valor = useMemo(() => ({
    estado,
    guiaActiva: !!estado,
    hayGuiaPospuesta,
    iniciarGuia,
    pararGuia,
    irAPaso,
    actualizarGuia,
    posponerGuia,
    retomarGuia,
    terminarGuia,
  }), [estado, hayGuiaPospuesta, iniciarGuia, pararGuia, irAPaso, actualizarGuia, posponerGuia, retomarGuia, terminarGuia]);

  return <GuiaContext.Provider value={valor}>{children}</GuiaContext.Provider>;
};
